import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  overrideAccountAlertSeverity,
  overrideOrganizationAlertSeverity,
  resetAccountAlertSeverity,
  resetOrganizationAlertSeverity,
} from '@kirin/api-client/api/alerts';
import type { SeverityOverrideState } from '@kirin/api-client/contracts/alerts';
import type { Severity } from '@kirin/api-client/contracts/shared/severity';
import { apiClient } from '@/lib/api-client';
import {
  invalidateAffectedQueries,
  type AlertSuppressionScope,
} from './alertSuppressionCache';

/**
 * Scope discriminator for the severity-override mutations (KIR-2364).
 * Re-exported as an alias of {@link AlertSuppressionScope} so the override cell
 * shares one scope type with the mute/exclude controls.
 *
 * - `organization`: org-shared alert. The caller must already be authorized
 *   (owner/manager) before rendering the control.
 * - `account`: the authenticated user's own alerts.
 */
export type AlertSeverityScope = AlertSuppressionScope;

/** Variables for {@link useOverrideAlertSeverity}. */
export interface OverrideAlertSeverityVariables {
  alertKey: string;
  severity: Severity;
}

/** Variables for {@link useResetAlertSeverity}. */
export interface ResetAlertSeverityVariables {
  alertKey: string;
}

/**
 * Override an alert group's severity (KIR-2364).
 *
 * Deliberately NOT optimistic: unlike mute, the effective severity depends on
 * a server-side `COALESCE` and the response also carries `originalSeverity` /
 * actor metadata the client can't synthesize, so we let the row refetch after
 * the write. Because nothing is patched locally, a failed write needs no
 * rollback — the badge is unchanged until the invalidation refetch confirms it.
 * `onSettled` invalidates the alerts lists + occurrences, USP and the
 * dashboard Top Alerts widget so the effective severity is refetched
 * everywhere it is read (KIR-5205).
 */
export function useOverrideAlertSeverity(
  scope: AlertSeverityScope
): UseMutationResult<
  SeverityOverrideState,
  Error,
  OverrideAlertSeverityVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ alertKey, severity }) => {
      if (scope.kind === 'organization') {
        return overrideOrganizationAlertSeverity(
          apiClient,
          scope.organizationId,
          alertKey,
          { severity }
        );
      }
      return overrideAccountAlertSeverity(apiClient, alertKey, { severity });
    },
    onSettled: (_data, _error, variables) =>
      invalidateAffectedQueries(
        queryClient,
        scope,
        variables.alertKey,
        'severity'
      ),
  });
}

/**
 * Reset an alert group's severity to Kirin's default (KIR-2364). Same
 * non-optimistic, refetch-on-settle strategy as {@link useOverrideAlertSeverity}.
 */
export function useResetAlertSeverity(
  scope: AlertSeverityScope
): UseMutationResult<
  SeverityOverrideState,
  Error,
  ResetAlertSeverityVariables
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ alertKey }) => {
      if (scope.kind === 'organization') {
        return resetOrganizationAlertSeverity(
          apiClient,
          scope.organizationId,
          alertKey
        );
      }
      return resetAccountAlertSeverity(apiClient, alertKey);
    },
    onSettled: (_data, _error, variables) =>
      invalidateAffectedQueries(
        queryClient,
        scope,
        variables.alertKey,
        'severity'
      ),
  });
}
